/**
 * Win/loss tally + current streak for the history-tab header badge
 * (docs/UI_REFERENCE.md "フィードバック反映" #1): "X勝Y敗・Z連勝中".
 */

export interface WinLossSummary {
  readonly wins: number;
  readonly losses: number;
  /** Consecutive win/loss count looking back from the newest hand. 0 if the newest hand was a chop or there are no hands. */
  readonly streak: number;
  readonly streakType: "win" | "loss" | null;
}

/**
 * `hands` must be newest-first (as returned by `getRecentHands`). A hero
 * net result of exactly 0 (chop) counts as neither a win nor a loss, and
 * -- as a deliberate simplification -- breaks the streak: it's not a win
 * or a loss to extend one, and treating it as a transparent no-op would
 * conflate "chopped one hand mid-heater" with an uninterrupted streak.
 */
export function computeWinLossSummary(
  hands: readonly { readonly heroNetChips: number }[]
): WinLossSummary {
  let wins = 0;
  let losses = 0;
  for (const h of hands) {
    if (h.heroNetChips > 0) wins++;
    else if (h.heroNetChips < 0) losses++;
  }

  let streak = 0;
  let streakType: "win" | "loss" | null = null;
  for (const h of hands) {
    if (h.heroNetChips === 0) break;
    const type: "win" | "loss" = h.heroNetChips > 0 ? "win" : "loss";
    if (streakType === null) {
      streakType = type;
    } else if (type !== streakType) {
      break;
    }
    streak++;
  }

  return { wins, losses, streak, streakType };
}
